import {
  IPublicClientApplication,
  AccountInfo,
} from '@azure/msal-browser';
import { graphTokenRequest, graphDirectoryRequest } from '../config/auth';

// Client-side Microsoft Graph directory lookup for the user picker.
//
// MSAL instance/account source: the app creates a single PublicClientApplication
// in App.tsx and is the owner of the active account (it calls setActiveAccount
// after the login redirect). To reuse THAT exact instance without spinning up a
// second one, the app registers it here once via registerMsal(instance). We then
// pull the active account off the shared instance. If the app has not registered
// the instance yet (or there's no account), getOrgUsers() returns [] gracefully.
//
// REQUIRED WIRING (App lane, App.tsx): after creating `msalInstance`, call
//   registerMsal(msalInstance)
// once (e.g. right after `const msalInstance = new PublicClientApplication(...)`).
// This is the only cross-lane touch point; see report.
let msalRef: IPublicClientApplication | null = null;
export const registerMsal = (instance: IPublicClientApplication): void => {
  msalRef = instance;
};

const GRAPH = 'https://graph.microsoft.com/v1.0';
const GRAPH_USERS_URL =
  GRAPH + '/users?$select=id,displayName,mail,userPrincipalName&$top=999';

interface GraphUser {
  id: string;
  displayName?: string;
  mail?: string | null;
  userPrincipalName?: string | null;
}

// Org user incl. the AAD object id — needed to intersect with app-role
// assignments (which key on principalId).
export interface OrgUser { id: string; name: string; email: string; }

// Resolve the account to use: prefer the instance's active account (set by the
// app after login), else the first known account. null if none.
const resolveAccount = (instance: IPublicClientApplication): AccountInfo | null => {
  const active = instance.getActiveAccount();
  if (active) return active;
  const all = instance.getAllAccounts();
  return all.length > 0 ? all[0] : null;
};

// Acquire a Microsoft Graph access token for the given scopes, SILENTLY (never a
// popup — these run as background fetches and a gesture-less popup hangs). Always
// resolves within the timeout: null on any failure (no instance/account, consent
// not propagated, blocked iframe, error) so callers degrade gracefully.
const getGraphToken = async (scopes: string[], timeoutMs = 8000): Promise<string | null> => {
  const work = (async (): Promise<string | null> => {
    const instance = msalRef;
    if (!instance) { console.warn('[graph] no MSAL instance registered'); return null; }
    const account = resolveAccount(instance);
    if (!account) { console.warn('[graph] no MSAL account'); return null; }
    try {
      const res = await instance.acquireTokenSilent({ scopes, account });
      return res.accessToken || null;
    } catch (e) {
      console.warn('[graph] acquireTokenSilent failed for', scopes.join(','), ':', (e as any)?.errorCode || e);
      return null;
    }
  })();
  const timeout = new Promise<string | null>((resolve) => setTimeout(() => resolve(null), timeoutMs));
  return Promise.race([work, timeout]);
};

// Fetch the org directory from Microsoft Graph and map to the picker shape.
// Never throws and ALWAYS resolves (hard timeout): any failure or hang (no
// registered instance, no account, consent not propagated, blocked silent-token
// iframe, network/Graph error) resolves to [] within the timeout so callers fall
// back to the history list and the picker is never stuck "Loading…".
//
// SILENT ONLY — never an interactive popup here: this runs as a background fetch
// (on mount / picker open), and a popup with no user gesture is blocked/hangs by
// the browser (that was the bug). The Graph scope is requested at login (see
// loginRequest), so once admin consent is granted acquireTokenSilent succeeds
// from cache without an iframe/popup.
export const getOrgUsers = async (): Promise<OrgUser[]> => {
  const token = await getGraphToken(graphTokenRequest.scopes);
  if (!token) return [];
  try {
    // The ONE sanctioned non-axios call: Microsoft Graph has a different
    // audience than our Functions API, so it must NOT use the axios `api` client.
    const response = await fetch(GRAPH_USERS_URL, {
      headers: { Authorization: 'Bearer ' + token },
    });
    if (!response.ok) {
      console.warn('[graph] /users HTTP', response.status, (await response.text()).slice(0, 300));
      return [];
    }
    const body = await response.json();
    const users: GraphUser[] = Array.isArray(body?.value) ? body.value : [];
    console.log('[graph] org users:', users.length);
    return users.map((u) => ({
      id: u.id,
      name: u.displayName || '',
      email: u.mail || u.userPrincipalName || '',
    }));
  } catch (e) {
    console.warn('[graph] error:', e);
    return [];
  }
};

// The set of AAD user object-ids that hold an app role (SALES / MANAGER / ADMIN)
// on THIS app — used to limit the View-as / Assign pickers to people who can
// actually act. Returns null when it can't be determined (Directory.Read.All not
// consented, or any error) so callers fall back to the full directory instead of
// an empty list. Only DIRECT user assignments are counted; roles granted via a
// group would need group expansion (not handled — assign roles to users).
export const getRoleAssignedUserIds = async (): Promise<Set<string> | null> => {
  const clientId = process.env.REACT_APP_AZURE_CLIENT_ID || '0d41cd70-b5b4-4ff1-a706-41d14867adc0';
  const token = await getGraphToken(graphDirectoryRequest.scopes);
  if (!token) return null;
  const headers = { Authorization: 'Bearer ' + token };
  try {
    // 1. This app's service principal: its object id + appRole definitions.
    const spResp = await fetch(
      `${GRAPH}/servicePrincipals(appId='${clientId}')?$select=id,appRoles`,
      { headers }
    );
    if (!spResp.ok) { console.warn('[graph] servicePrincipal lookup HTTP', spResp.status); return null; }
    const sp = await spResp.json();
    const spId: string | undefined = sp?.id;
    if (!spId) { console.warn('[graph] no service principal id'); return null; }
    const validRoleIds = new Set<string>((sp.appRoles || []).map((r: any) => r.id));

    // 2. Walk appRoleAssignedTo (paged) and collect USER principals whose
    //    assignment is one of the app's real roles.
    const ids = new Set<string>();
    let url: string = `${GRAPH}/servicePrincipals/${spId}/appRoleAssignedTo?$top=999`;
    let guard = 0;
    while (url && guard++ < 20) {
      const r = await fetch(url, { headers });
      if (!r.ok) { console.warn('[graph] appRoleAssignedTo HTTP', r.status); return null; }
      const j = await r.json();
      for (const a of (j.value || [])) {
        if (a.principalType === 'User' && validRoleIds.has(a.appRoleId)) ids.add(a.principalId);
      }
      url = j['@odata.nextLink'] || '';
    }
    console.log('[graph] role-assigned users:', ids.size);
    return ids;
  } catch (e) {
    console.warn('[graph] role assignments error:', e);
    return null;
  }
};
